/* eslint-disable @typescript-eslint/naming-convention */
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { isNil } from "ramda";
import { orderBy } from "lodash";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { Data } from "../../../store/user/types";
import { deleteRequest as deleteUser } from "../../../store/user/actions";
import Confirm from "../../../common/confirm/Confirm";
import { MRT_ColumnDef, MaterialReactTable, MRT_PaginationState, MRT_SortingState } from "material-react-table";
import TableActions from "../../../common/table/TableActions";

interface Props {
  user?: Data[];
  pagination: MRT_PaginationState;
  setPagination?: (values: MRT_PaginationState) => void;
  page: number | undefined;
  totalUsers: number | undefined;
  pageSize: number | undefined;
  onPageChanged: (values: { pageIndex: number, pageSize: number }) => void;
}

interface RowData {
  id: string;
  name: string;
  email: string;
  group: string;
  lastLogin: string;
  lastLoginEpoch: number;
}

const mapDataToTable = (users: Data[]): RowData[] => {
  return users.map((data) => {
    const attributes = data.attributes;
    const lastName = attributes.LastName || "";
    const name = `${attributes.FirstName} ${lastName}`;

    const lastLogin = !isNil(attributes.LastLogin)
      ? moment(attributes.LastLogin).format("DD/MM/YYYY HH:mm:SS a")
      : "";
    const lastLoginEpoch = !isNil(attributes.LastLogin)
      ? moment(attributes.LastLogin).unix()
      : 0;
    return {
      id: data.id,
      name: name,
      email: attributes.Email,
      group: attributes.Group,
      lastLogin: lastLogin,
      lastLoginEpoch: lastLoginEpoch,
    };
  });
};

export default function UserTable({ user, totalUsers, pagination, setPagination, onPageChanged }: Props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [ showConfirm, setShowConfirm ] = useState(false);
  const [ currentId, setCurrentId ] = useState<string | null>(null);
  const data = mapDataToTable(orderBy(user, "attributes.name_sorted"));

  const [ sorting, setSorting ] = useState<MRT_SortingState>([]);

  const editAction = (rowData: RowData) => {
    history.push(`/user/edit/${rowData.id}`);
  };

  const viewAction = (rowData: RowData) => {
    history.push(`/user/view/${rowData.id}`);
  };

  const deleteAction = (rowData: RowData) => {
    setCurrentId(rowData.id);
    setShowConfirm(true);
  };
  const deleteConfirmationHandler = () => {
    const callback = (error: string | null) => {
      if (error) {
        console.log(error);
      }
    };
    !isNil(currentId) && dispatch(deleteUser(currentId, callback));
    setCurrentId(null);
  };

  const columns = useMemo<MRT_ColumnDef<RowData>[]>(
    () => [
      { header: "Name", accessorKey: "name" },
      {
        header: "Email",
        accessorKey: "email",
      },
      {
        header: "Role",
        accessorKey: "group",
      },
      {
        header: "Last Login",
        accessorKey: "lastLogin",
        size: 200,
        // customSort: (data1, data2, type) => {
        //   return data1.lastLoginEpoch - data2.lastLoginEpoch;
        // },
      },
    ],
    []
  );

  return (
    <>
      <MaterialReactTable
        columns={columns}
        data={mapDataToTable(user)}
        getRowId={(row) => row.id}
        initialState={{
          showColumnFilters: false,
          density: "spacious",
        }}
        enableTopToolbar={false}
        enableColumnFilters={false}
        enableColumnActions={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableRowActions
        manualFiltering
        manualPagination
        onPaginationChange={onPageChanged}
        onSortingChange={setSorting}
        rowCount={totalUsers}
        muiTablePaginationProps={{
          rowsPerPageOptions: [ 10, 20, 40, 60 ],
        }}
        state={{
          pagination,
          sorting,
        }}
        displayColumnDefOptions={{
          "mrt-row-actions": {
            header: "",
            size: 100,
          },
        }}
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <TableActions
            section="User"
            onView={() => viewAction(row.original)}
            onEdit={() => editAction(row.original)}
            onDelete={() => deleteAction(row.original)}
          />
        )}
      />

      <Confirm
        isOpen={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onConfirm={() => deleteConfirmationHandler()}
        title="Delete User"
        icon="danger"
        confirmLabel="Delete User"
        contentText={`You are about to delete User`}
      />
    </>
  );
}
